import { useEffect, useState } from "react"

/**
 * Tracks which of the given sections the reader is currently in: the last
 * section whose top has crossed a line `activationRatio` down the viewport.
 * The first section is active until any section crosses that line, and the
 * last section wins once the page is scrolled to the bottom, so every menu
 * entry can become active even when the final section is shorter than the
 * viewport.
 */
export function useActiveSection(
  sectionIds: readonly string[],
  activationRatio = 0.4,
): string | null {
  const [activeId, setActiveId] = useState<string | null>(sectionIds[0] ?? null)
  const sectionKey = sectionIds.join(" ")

  useEffect(() => {
    const ids = sectionKey.split(" ").filter(Boolean)
    let frame: number | null = null

    const update = () => {
      frame = null
      const scrolledToBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2
      if (scrolledToBottom) {
        setActiveId(ids.at(-1) ?? null)
        return
      }

      const line = window.innerHeight * activationRatio
      let current = ids[0] ?? null
      for (const id of ids) {
        const element = document.getElementById(id)
        if (element && element.getBoundingClientRect().top <= line) current = id
      }
      setActiveId(current)
    }

    const schedule = () => {
      if (frame !== null) return
      frame = window.requestAnimationFrame(update)
    }

    schedule()
    window.addEventListener("scroll", schedule, { passive: true })
    window.addEventListener("resize", schedule)

    return () => {
      window.removeEventListener("scroll", schedule)
      window.removeEventListener("resize", schedule)
      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [sectionKey, activationRatio])

  return activeId
}
